import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { api } from '../api/client';
import { apiErrorText } from '../api/errorText';
import type { Library } from '../api/types';
import { useApi } from '../api/useApi';
import AsyncSection from '../components/AsyncSection';
import LibrarySourceFields from '../components/libraries/LibrarySourceFields';
import { locationsFor, sourceName } from '../components/libraries/librarySources';
import { supportedLanguages } from '../i18n';

/// One library: what it is called, where it reads from, in which language it is described, who
/// describes it, and the two things somebody comes here to press. Scanning and describing live
/// here rather than on a film, because they are properties of the shelf and not of what is on it.
/// Removing it lives here too, and nowhere else.
export default function LibrarySettingsPage() {
  const { libraryId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const library = useApi(['libraries', libraryId], () => api.library(libraryId));
  const providers = useApi(['libraries', 'metadata', 'providers'], api.metadataProviders);
  const sources = useApi(['libraries', 'providers'], api.mediaSourceProviders);

  const [removing, setRemoving] = useState(false);
  const [removeBusy, setRemoveBusy] = useState(false);

  const [draft, setDraft] = useState<Library>();
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState<'scan' | 'metadata'>();

  const current = draft ?? library.data;

  // A library reads from one source, so the first location names it for all of them - taken from
  // what is saved, so unticking everything in the draft does not take the source's fields away.
  const source = sources.data?.find(
    (candidate) => candidate.providerId === library.data?.locations[0]?.providerId,
  );

  const setAddresses = (addresses: string[]) => {
    if (!current || !source) {
      return;
    }

    // As typed: a path is trimmed when it is saved, not while somebody is still typing the space
    // in "My Movies". Ids are settled on saving too.
    setDraft({
      ...current,
      locations: [
        ...addresses.map((address) => ({ id: '', providerId: source.providerId, address })),
        ...current.locations.filter((location) => location.providerId !== source.providerId),
      ],
    });
  };

  const save = async () => {
    if (!current) {
      return;
    }

    setBusy(true);
    setSaved(false);
    setError(undefined);

    // Folders are typed, so a field left empty is one that was never filled in, not a location.
    const providerId = source?.providerId ?? current.locations[0]?.providerId ?? '';
    const locations = [
      ...locationsFor(
        providerId,
        current.locations.filter((location) => location.providerId === providerId).map((location) => location.address),
        library.data?.locations,
      ),

      // A library made before a library had one source may read from another as well. What
      // this page does not edit, it passes through as it was; the server says what to do about it.
      ...current.locations.filter((location) => location.providerId !== providerId),
    ];
    const before = new Set(library.data?.locations.map((location) => location.id));
    const gained = locations.some((location) => !before.has(location.id));

    try {
      await api.saveLibrary({ ...current, locations });

      // What was added is read now rather than on the next scheduled scan.
      if (gained) {
        await api.scanLibrary(current.id);
      }

      setDraft(undefined);
      library.reload();
      setSaved(true);
    } catch (caught) {
      setError(apiErrorText(caught, t).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setRemoveBusy(true);

    try {
      await api.deleteLibrary(libraryId);
      navigate('/libraries');
    } catch (caught) {
      setRemoving(false);
      setError(apiErrorText(caught, t).message);
    } finally {
      setRemoveBusy(false);
    }
  };

  /// Empty means every provider there is, so ticking none and ticking all are the same thing -
  /// and the empty one is the one that keeps working when a plugin is installed later.
  const chosen = (providerId: string) =>
    (current?.metadataProviders.length ?? 0) === 0 || current!.metadataProviders.includes(providerId);

  const toggle = (providerId: string) => {
    if (!current) {
      return;
    }

    const all = (providers.data ?? []).map((provider) => provider.providerId);
    const selected = current.metadataProviders.length === 0 ? all : current.metadataProviders;

    const next = selected.includes(providerId)
      ? selected.filter((id) => id !== providerId)
      : all.filter((id) => selected.includes(id) || id === providerId);

    setDraft({ ...current, metadataProviders: next.length === all.length ? [] : next });
  };

  return (
    <AsyncSection loading={library.loading} error={library.error} onRetry={library.reload}>
      {current && (
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
            <Typography variant="h2" sx={{ flexGrow: 1 }}>
              {current.name}
            </Typography>
            <Chip label={t(`libraryKind.${current.kind}`)} />
          </Stack>

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={3}>
                {error && <Alert severity="error">{error}</Alert>}
                {saved && <Alert severity="success">{t('settings.saved')}</Alert>}

                <TextField
                  label={t('library.name')}
                  value={current.name}
                  onChange={(event) => setDraft({ ...current, name: event.target.value })}
                />

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {source ? t('libraries.sourceNamed', { name: sourceName(t, source) }) : t('libraries.source')}
                  </Typography>

                  <AsyncSection loading={sources.loading} error={sources.error} onRetry={sources.reload}>
                    {source && (
                      <LibrarySourceFields
                        provider={source}
                        kind={current.kind}
                        addresses={current.locations
                          .filter((location) => location.providerId === source.providerId)
                          .map((location) => location.address)}
                        onChange={setAddresses}
                      />
                    )}
                  </AsyncSection>
                </Box>

                <TextField
                  select
                  label={t('library.metadataLanguage')}
                  helperText={t('library.metadataLanguageHint')}
                  value={current.metadataLanguage}
                  onChange={(event) => setDraft({ ...current, metadataLanguage: event.target.value })}
                >
                  {supportedLanguages.map((language) => (
                    <MenuItem key={language.code} value={language.code}>
                      {language.label}
                    </MenuItem>
                  ))}
                </TextField>

                <Box>
                  <Typography variant="subtitle2">{t('library.metadataProviders')}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('library.metadataProvidersHint')}
                  </Typography>

                  <AsyncSection
                    loading={providers.loading}
                    error={providers.error}
                    onRetry={providers.reload}
                  >
                    {(providers.data ?? []).length === 0 ? (
                      <Typography color="text.secondary">{t('library.noMetadataProviders')}</Typography>
                    ) : (
                      <Stack>
                        {providers.data?.map((provider) => (
                          <FormControlLabel
                            key={provider.providerId}
                            control={
                              <Checkbox
                                checked={chosen(provider.providerId)}
                                onChange={() => toggle(provider.providerId)}
                              />
                            }
                            label={provider.name}
                          />
                        ))}
                      </Stack>
                    )}
                  </AsyncSection>
                </Box>

                <Box>
                  <Button variant="contained" onClick={() => void save()} disabled={busy}>
                    {t('common.save')}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="subtitle2">{t('library.maintenance')}</Typography>

                <Typography variant="body2" color="text.secondary">
                  {t('library.lastScan')}:{' '}
                  {current.lastScannedAt
                    ? new Date(current.lastScannedAt).toLocaleString(i18n.language)
                    : t('libraries.never')}
                </Typography>

                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }} useFlexGap>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setStarted('scan');
                      void api.scanLibrary(current.id);
                    }}
                  >
                    {t('libraries.scan')}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => {
                      setStarted('metadata');
                      void api.refreshLibraryMetadata(current.id, true);
                    }}
                  >
                    {t('libraries.refreshMetadata')}
                  </Button>
                </Stack>

                {started && <Alert severity="info">{t(`library.started.${started}`)}</Alert>}

                <Typography variant="body2" color="text.secondary">
                  {t('library.periodicHint')}
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
                <Typography variant="subtitle2">{t('libraries.remove')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('libraries.remove.hint')}
                </Typography>
                <Button variant="outlined" color="error" onClick={() => setRemoving(true)}>
                  {t('libraries.remove')}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Dialog open={removing} onClose={removeBusy ? undefined : () => setRemoving(false)} fullWidth maxWidth="xs">
            <DialogTitle>{t('libraries.remove.title')}</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ overflowWrap: 'anywhere' }}>
                {t('libraries.remove.body', { name: current.name })}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRemoving(false)} disabled={removeBusy}>
                {t('common.cancel')}
              </Button>
              <Button
                color="error"
                variant="contained"
                disabled={removeBusy}
                startIcon={removeBusy ? <CircularProgress size={16} color="inherit" /> : undefined}
                onClick={() => void remove()}
              >
                {t('libraries.remove.confirm')}
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      )}
    </AsyncSection>
  );
}
